import React, {useContext, useState} from "react";
import {Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {AlertCircleIcon, ArrowRight01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {c, primaryButtonSx} from "../../landing/landingTokens";
import {AuthLayout} from "../components/AuthLayout";
import {AuthPasswordField} from "../components/AuthPasswordField";
import {PasswordChecklist, isPasswordValid} from "../components/PasswordChecklist";

export const ChangePasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({password: "", confirmPassword: ""});
  const [loading, setLoading] = useState(false);
  const {onChangePassword} = useContext(SignInContext);
  const navigate = useNavigate();
  const search = useLocation().search;
  const token = new URLSearchParams(search).get("token");

  const validate = () => {
    const nextErrors = {password: "", confirmPassword: ""};
    if (!password) {
      nextErrors.password = "Password is required";
    } else if (!isPasswordValid(password)) {
      nextErrors.password = "Password doesn't meet all the requirements";
    }
    if (!confirmPassword) {
      nextErrors.confirmPassword = "Please confirm your password";
    } else if (password && confirmPassword !== password) {
      nextErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(nextErrors);
    return !nextErrors.password && !nextErrors.confirmPassword;
  };

  const loginHandle = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const toastId = toast.loading("Sending request to change password");
    setLoading(true);
    try {
      await onChangePassword({password, token});
      toast.update(toastId, {
        type: "success",
        render: "Password changed successfully, please check your email address for confirmation",
        isLoading: false,
        autoClose: 2000,
      });
      setPassword("");
      setConfirmPassword("");
      navigate("/login");
    } catch (err) {
      toast.update(toastId, {
        type: "error", render: err?.message || "Please try again later.", isLoading: false, autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={"Set new password"}
      prompt={"Remember your password?"}
      linkText={"Sign in"}
      linkTo={"/login"}
      preserveSearch={false}
    >
      <Stack component={"form"} onSubmit={loginHandle} direction={"column"} gap={0.5} sx={{width: "100%"}}>
        {!token && (
          <Stack direction={"row"} gap={1.5} alignItems={"flex-start"} sx={{
            bgcolor: "rgba(129,140,248,0.09)", borderRadius: "16px", px: 2, py: 1.75, mb: 1.5,
          }}>
            <Box sx={{
              width: 32, height: 32, flexShrink: 0, borderRadius: "10px", display: "flex",
              alignItems: "center", justifyContent: "center", bgcolor: "rgba(129,140,248,0.16)",
            }}>
              <HugeiconsIcon icon={AlertCircleIcon} size={18} color={c.purpleDeep}/>
            </Box>
            <Box>
              <Typography sx={{fontSize: 14, fontWeight: 600, color: c.ink, lineHeight: 1.4}}>
                This reset link isn't valid
              </Typography>
              <Typography sx={{fontSize: 13, color: c.inkSoft, lineHeight: 1.5, mt: 0.25}}>
                It may be missing or expired.
              </Typography>
              <Link to={"/forgot-password"} style={{
                display: "inline-block", marginTop: 6, fontSize: 13, color: c.purpleDeep,
                fontWeight: 600, textDecoration: "none"
              }}>
                Request a new link
              </Link>
            </Box>
          </Stack>
        )}
        <AuthPasswordField
          autoFocus
          required
          disabled={!token}
          label={"Password"}
          value={password}
          onChange={e => {
            setPassword(e.target.value);
            setErrors(prev => ({...prev, password: ""}));
          }}
          placeholder={"Enter your new password"}
          error={!!errors.password}
          helperText={errors.password || " "}
        />
        <PasswordChecklist password={password} showErrors={!!errors.password}/>
        <AuthPasswordField
          required
          disabled={!token}
          sx={{mt: 1}}
          label={"Confirm Password"}
          value={confirmPassword}
          onChange={e => {
            setConfirmPassword(e.target.value);
            setErrors(prev => ({...prev, confirmPassword: ""}));
          }}
          placeholder={"Re-enter your new password"}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword || " "}
        />

        <Button
          fullWidth
          type={"submit"}
          disabled={loading || !token || !password || !confirmPassword || password !== confirmPassword}
          sx={{...primaryButtonSx, py: 1.4, fontSize: 15, mt: 1}}
          endIcon={loading
            ? <CircularProgress size={16} color={"inherit"}/>
            : <HugeiconsIcon icon={ArrowRight01Icon} size={13}/>}
        >
          Continue
        </Button>
      </Stack>
    </AuthLayout>
  );
};
